import _ from 'lodash'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { io, Socket } from 'socket.io-client'

import { devicesList, devicesSave, devicesUpdate, msvStart, streamsList, streamStart, streamStop, test } from './actions'
import { RpiActionParams, RpiDevice, RpiRoutes, RpiData, RpiStreamInfo, RpiHealthcheck, RpiCurrentStream, RpiRecordParams } from './types'
import { createReducer } from '@reduxjs/toolkit'

export type RpiContextType = {
  currentDeviceId: string | null
  currentStreams: Record<string, RpiCurrentStream>
  devices: RpiDevice[]
  error?: Error
  /**
   * Ask rpi if device is ready to stream
   * @param id device id
   */
  isDeviceAlive: (id: string) => void
  /**
   * ask service for deviceList
   */
  getDeviceList: () => void
  setCurrentDevice: (deviceId: string) => void
  /**
   * Ask service to run tests
   */
  getTestResults: () => void
  /**
   * ask service for streamList
   */
  getStreamList: () => void
  join: (id: string) => void
  msv: {
    connected: boolean
    values: Array<Record<string, any>>
  }
  requestCurrentStream: () => void
  /**
   * start Vital Signs monitor
   */
  startMSV: () => void
  /**
   * stop Vital Signs monitor
   */
  stopMSV: () => void
  /**
   * Start stream of given device id
   * @param id device id
   */
  startStream: (deviceId: string, resolution?: string, framerate?: string) => void
  /**
   * Stop Stream of given stream id
   * @param id stream ID
   */
  stopStream: (id: string) => void
  startRecording: (params: RpiRecordParams) => void
  streams: RpiStreamInfo[]
}

type RpiProviderProps = {
  children?: React.ReactNode
  isStreamer?: boolean
  meetingID: string | null
  serverUrl: string
}

// TODO: use for msv
// const msvMapping: Record<string, { label: string, unit: string, order: number }> = {
//   'spo_2': {
//     label: 'Saturation 02',
//     unit: '%',
//     order: 1
//   },
//   'bpm': {
//     label: 'Pulsations',
//     unit: 'bpm',
//     order: 2
//   },
//   // 't_high': {},
//   // 't_low': {},
//   'temp': {
//     label: 'Temperature',
//     unit: '°C',
//     order: 4
//   },
//   'gluc': {
//     label: 'Glycemie capillaire',
//     unit: 'mg/dL',
//     order: 7
//   },
//   'gluc_mol': {
//     label: 'Glycémie capillaire',
//     unit: 'mmol/dL',
//     order: 8
//   }
// }

type State = {
  currentDeviceId: string | null
  currentStreams: Record<string, RpiCurrentStream>
  devices: RpiDevice[]
  healthcheck: RpiHealthcheck | null
  streams: RpiStreamInfo[]
  msv: {
    connected: boolean
    values: Array<Record<string, any>>
  }
}

const initialState: State = {
  currentDeviceId: null,
  currentStreams: {},
  devices: [],
  healthcheck: null,
  msv: {
    connected: false,
    values: [],
  },
  streams: [],
}

const reducer = createReducer(initialState, (builder) => {
  builder
    .addCase(msvStart, (state, action) => {
      state.msv.connected = true
      state.msv.values = action.payload
    })
    .addCase('stop_msv', (state) => {
      state.msv.connected = false
      state.msv.values = []
    })
    .addCase('current_device', (state, action: { payload: string; type: 'current_device' }) => {
      state.currentDeviceId = action.payload
    })
    .addCase(streamsList, (state, action) => {
      state.streams = action.payload
    })
    .addCase(streamStart, (state, action) => {
      state.currentDeviceId = action.payload.deviceId
      state.currentStreams[action.payload.deviceId] = action.payload
    })
    .addCase(streamStop, (state, action) => {
      const { device } = action.payload
      if (state.currentStreams[device.id]) delete state.currentStreams[device.id]
    })
    .addCase(test, (state, action) => {
      state.healthcheck = action.payload
    })
    .addMatcher(
      (action) => [RpiRoutes.DEVICES, RpiRoutes.DEVICES_SAVE, RpiRoutes.DEVICES_UPDATE].includes(action.type),
      (state, action: ReturnType<typeof devicesList | typeof devicesSave | typeof devicesUpdate>) => {
        state.devices = action.payload.map((device) => ({
          ...device,
          resolution: _.sortBy(device.resolution, (resolution) => {
            const [resWidth, resHeight] = resolution.split('x')
            return parseInt(resWidth) * parseInt(resHeight)
          }),
        }))
      }
    )
})

// // TODO: replace by env variables
// const defaultRpiOption = {
//   meetingID: "",
//   server_url: "localhost:3333",
//   videoserver_url: "http://ab9a0b5f1a9b94856a318d044cf2ef59-1056024239.eu-west-3.elb.amazonaws.com",
// }

export const RpiProviderContext = createContext<RpiContextType | null>(null)

export function RpiProvider(props: RpiProviderProps) {
  const { children, isStreamer = false, meetingID, serverUrl } = props
  const [client, setClient] = useState<Socket>()
  const [error, setError] = useState<Error>()
  const [ready, setReady] = useState<boolean>(false)
  const [{ devices, streams, currentDeviceId, currentStreams, healthcheck, msv }, dispatch] = useReducer(reducer, initialState)
  const currentDeviceIdRef = useRef(currentDeviceId)
  const currentStreamsRef = useRef(currentStreams)

  const emitAction = useCallback(
    ({ room, route, arg }: RpiActionParams) => {
      client?.emit('action', { room, route, params: arg })
    },
    [client]
  )

  const getDeviceList = useCallback(() => {
    if (!meetingID || !ready) return
    const act: RpiActionParams = {
      route: RpiRoutes.DEVICES,
      room: meetingID,
    }
    emitAction(act)
  }, [emitAction, meetingID, ready])

  const isDeviceAlive = useCallback(
    (deviceId: string) => {
      if (!meetingID || !ready) return
      const act: RpiActionParams = {
        route: RpiRoutes.ALIVE,
        room: meetingID,
        arg: deviceId,
      }
      emitAction(act)
    },
    [emitAction, meetingID, ready]
  )

  const getStreamList = useCallback(() => {
    if (!meetingID || !ready) return
    const act: RpiActionParams = {
      route: RpiRoutes.STREAMS,
      room: meetingID,
    }
    emitAction(act)
  }, [emitAction, meetingID, ready])

  const setCurrentDevice = useCallback((deviceId: string) => {
    dispatch({ type: 'current_device', payload: deviceId })
  }, [dispatch])

  const startStream = useCallback(
    (deviceId: string, resolution?: string, framerate?: string) => {
      if (!meetingID || !ready) return
      const act: RpiActionParams = {
        route: RpiRoutes.STREAM_START,
        room: meetingID,
        arg: {
          deviceId,
          resolution,
          framerate,
        },
      }
      emitAction(act)
      setCurrentDevice(deviceId)
    },
    [emitAction, meetingID, ready, setCurrentDevice]
  )

  const stopStream = useCallback(
    (streamId: string) => {
      if (!meetingID || !ready) return
      const act: RpiActionParams = {
        route: RpiRoutes.STREAM_STOP,
        room: meetingID,
        arg: streamId,
      }
      emitAction(act)
    },
    [emitAction, meetingID, ready]
  )

  const getTestResults = useCallback(() => {
    if (!meetingID || !ready) return
    const act: RpiActionParams = {
      route: RpiRoutes.TEST,
      room: meetingID,
    }
    emitAction(act)
  }, [emitAction, meetingID, ready])

  const startMSV = useCallback(() => {
    if (!meetingID || !ready) return
    const act: RpiActionParams = {
      route: RpiRoutes.MSV,
      room: meetingID,
    }
    emitAction(act)
  }, [emitAction, meetingID, ready])

  const stopMSV = useCallback(() => {
    dispatch({ type: 'STOP_MSV' })
  }, [])

  const startRecording = useCallback(
    (params: RpiRecordParams) => {
      if (!meetingID || !ready) return
      emitAction({
        route: RpiRoutes.RECORD,
        room: meetingID,
        arg: params,
      })
    },
    [emitAction, meetingID, ready]
  )

  const join = useCallback(() => {
    if (!meetingID || !ready) return
    client?.emit('join', meetingID)
  }, [client, meetingID, ready])

  const requestCurrentStream = useCallback(() => {
    if (!meetingID || !ready) return
    client?.emit('request_current_stream', meetingID)
  }, [client, meetingID, ready])

  // create socket client
  useEffect(() => {
    setClient(io(serverUrl))
  }, [serverUrl])

  useEffect(() => {
    currentDeviceIdRef.current = currentDeviceId
  }, [currentDeviceId])

  // keep track of currentStream's value in a ref in order to stop stream when provider unmounts
  useEffect(() => {
    currentStreamsRef.current = currentStreams
  }, [currentStreams])

  useEffect(() => {
    return () => {
      if (isStreamer && currentDeviceIdRef.current && currentStreamsRef.current[currentDeviceIdRef.current]) {
        stopStream(currentStreamsRef.current[currentDeviceIdRef.current].streamId)
      }
    }
  }, [isStreamer, stopStream])

  // listen to socket event name
  useEffect(() => {
    if (!client) return

    client.on('connect', () => {
      console.log('connected', client.id)
      setError(undefined)
      setReady(true)
      if (meetingID) client.volatile.emit('join', meetingID)
    })

    client.on('connect_error', (err: Error) => {
      console.log('socket connect error', err)
      setError(err)
    })

    client.on('disconnect', (reason: string) => {
      console.log('socket disconnected', reason)
    })

    client.on('data', ({ route, data }: RpiData) => {
      console.log('Received data' + data)
      switch (route) {
        case RpiRoutes.DEVICES:
          dispatch(devicesList(data))
          break
        case RpiRoutes.DEVICES_SAVE:
          dispatch(devicesSave(data))
          break
        case RpiRoutes.DEVICES_UPDATE:
          dispatch(devicesUpdate(data))
          break
        case RpiRoutes.MSV:
          dispatch(msvStart(data))
          break
        case RpiRoutes.STREAMS:
          dispatch(streamsList(data))
          break
        case RpiRoutes.STREAM_START:
          dispatch(streamStart(data))
          break
        case RpiRoutes.STREAM_STOP:
          dispatch(streamStop(data))
          break
        case RpiRoutes.TEST:
          dispatch(test(data))
          break
        default:
          break
      }
      dispatch({ type: route, payload: data })
    })

    client.on('request_current_stream', () => {
      if (isStreamer && !!meetingID) {
        client.emit('response_current_stream', {
          room: meetingID,
          currentStream: currentDeviceIdRef.current ? currentStreamsRef.current[currentDeviceIdRef.current] : null,
        })
      }
    })

    client.on('response_current_stream', (currentStream: RpiCurrentStream | null) => {
      if (currentStream) dispatch(streamStart(currentStream))
    })

    return () => {
      client.disconnect()
    }
  }, [client, isStreamer, meetingID])

  const value = useMemo(
    () => ({
      getDeviceList,
      isDeviceAlive,
      getStreamList,
      setCurrentDevice,
      startStream,
      stopStream,
      getTestResults,
      startMSV,
      stopMSV,
      join,
      requestCurrentStream,
      startRecording,
      currentDeviceId,
      currentStreams,
      devices,
      healthcheck,
      msv,
      streams,
      error,
    }),
    [
      getDeviceList,
      isDeviceAlive,
      getStreamList,
      setCurrentDevice,
      startStream,
      stopStream,
      getTestResults,
      startMSV,
      stopMSV,
      join,
      requestCurrentStream,
      startRecording,
      currentDeviceId,
      currentStreams,
      devices,
      healthcheck,
      msv,
      streams,
      error,
    ]
  )

  return <RpiProviderContext.Provider value={value}>{children}</RpiProviderContext.Provider>
}

export const useRpi = (): RpiContextType => {
  const context = useContext(RpiProviderContext)

  if (!context) {
    throw new Error('useRpi must be used within a RpiProvider')
  }

  return context
}

export default RpiProvider
